const SCROLL_ANIMATION_TRIGGER_CLASSNAME = "scroll-trigger",
  SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = "scroll-trigger--offscreen",
  SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = "animate--zoom-in",
  SCROLL_ANIMATION_CANCEL_CLASSNAME = "scroll-trigger--cancel";

/* SOTG: reveal reliability fixes — see QA item 7.
   The IntersectionObserver callback that removes/adds the offscreen class can be slow
   or never fire promptly (deferred-script backlog, throttled tabs, etc.), so content
   already in the viewport on first paint could stay hidden (opacity ~0) for many
   seconds. Three fixes below:
   1. prefersReducedMotion() short-circuits everything to "already revealed".
   2. initializeScrollAnimationTrigger checks each element's current position with
      getBoundingClientRect BEFORE handing it to the observer, so anything already
      on screen is revealed synchronously instead of waiting on a callback.
   3. armRevealFailsafe() force-reveals every .scroll-trigger element 1200ms after
      load, no matter what the observer did or didn't do. */
function prefersReducedMotion() {
  return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function revealElement(element, index) {
  element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  if (element.hasAttribute("data-cascade") && typeof index === "number") {
    element.setAttribute("style", `--animation-order: ${index};`);
  }
}

function isInViewportNow(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  // Mirrors the observer's rootMargin: "0px 0px -50px 0px" (bottom edge pulled in 50px).
  return rect.top < viewportHeight - 50 && rect.bottom > 0 && rect.left < viewportWidth && rect.right > 0;
}

function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    if (entry.isIntersecting) {
      const element = entry.target;
      if (element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        revealElement(element, index);
      }
      observer.unobserve(element);
    } else {
      entry.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      entry.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function armRevealFailsafe() {
  setTimeout(() => {
    document.querySelectorAll(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`).forEach((element) => {
      if (element.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        revealElement(element);
      }
    });
  }, 1200);
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const elements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (elements.length === 0) return;

  if (isDesignModeEvent) {
    elements.forEach((element) => {
      element.classList.add("scroll-trigger--design-mode");
    });
    return;
  }

  if (prefersReducedMotion()) {
    elements.forEach((element) => revealElement(element));
    return;
  }

  const observer = new IntersectionObserver(onIntersection, { rootMargin: "0px 0px -50px 0px" });
  elements.forEach((element) => {
    // Already on screen at first paint — reveal now rather than waiting for the
    // observer's first (async, sometimes late) callback.
    if (isInViewportNow(element)) {
      revealElement(element);
    } else {
      observer.observe(element);
    }
  });

  armRevealFailsafe();
}

function initializeScrollZoomAnimationTrigger() {
  if (prefersReducedMotion()) return;
  const elements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (elements.length === 0) return;

  elements.forEach((element) => {
    let isIntersecting = false;
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isIntersecting = entry.isIntersecting;
      });
    }).observe(element);

    element.style.setProperty("--zoom-in-ratio", 1 + 0.002 * percentageSeen(element));
    window.addEventListener(
      "scroll",
      throttle(() => {
        if (isIntersecting) {
          element.style.setProperty("--zoom-in-ratio", 1 + 0.002 * percentageSeen(element));
        }
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  }
  if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

function runInitialScrollAnimations() {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
}

// SOTG: don't wait on DOMContentLoaded alone — this is a deferred script, so by the
// time it runs the DOM may already be parsed (readyState !== "loading"), in which
// case DOMContentLoaded has already fired or is about to and waiting for it only adds
// avoidable delay before in-viewport content gets revealed.
if (document.readyState === "loading") {
  window.addEventListener("DOMContentLoaded", runInitialScrollAnimations);
} else {
  runInitialScrollAnimations();
}

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) =>
    initializeScrollAnimationTrigger(event.target, true)
  );
  document.addEventListener("shopify:section:reorder", () =>
    initializeScrollAnimationTrigger(document, true)
  );
}
